<template>
  <div class="store-selection-step card-base fade-in">
    <div class="card-body p-4">
      <h4 class="card-title mb-4 text-uppercase">
        <span class="gradient-text">Select</span> <span class="text-primary-color">Store</span>
      </h4>

      <!-- Search Bar -->
      <div class="search-container mb-3">
        <div class="search-input-wrapper">
          <font-awesome-icon icon="fa-solid fa-search" class="search-icon" />
          <input 
            type="text" 
            class="search-input"
            v-model="searchQuery"
            placeholder="Search stores..."
          >
          <button 
            v-if="searchQuery" 
            @click="searchQuery = ''" 
            class="clear-search-btn"
            type="button"
          >
            <font-awesome-icon icon="fa-solid fa-times" />
          </button>
        </div>
      </div>

      <!-- Sign Out Button -->
      <div class="sign-out-container">
        <button class="sign-out-btn" @click="logout" type="button">
          <font-awesome-icon icon="fa-solid fa-arrow-right-from-bracket" />
          <span>Sign Out</span>
        </button>
      </div>

      <form @submit.prevent="handleStoreSelection" class="store-form">
        <div class="form-group mb-4">
          <label class="store-selection-label">Select Your Store</label>
          <div class="store-list-container">
            <div v-if="filteredStores.length === 0" class="no-results">
              <font-awesome-icon icon="fa-solid fa-store-slash" class="no-results-icon" />
              <p>No stores found matching "{{ searchQuery }}"</p>
            </div>

            <div v-else class="store-list">
              <div
                v-for="(store, n) in filteredStores"
                :key="n"
                class="store-card"
                :class="{ 'selected': selectedStore && selectedStore.StoreName === store.StoreName }"
                @click="selectStore(store)"
              >
                <div class="store-icon">
                  <font-awesome-icon icon="fa-solid fa-store" />
                </div>
                <div class="store-info">
                  <div class="store-name">{{ store.StoreName }}</div>
                </div>
                <div class="store-checkmark" v-if="selectedStore && selectedStore.StoreName === store.StoreName">
                  <font-awesome-icon icon="fa-solid fa-check" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="text-center">
          <button :disabled="loading || !selectedStore" type="submit" class="store-button">
            <span class="button-text">{{ loadText }}</span>
            <span v-if="loading" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Storage } from '@/services/Storage';
import { Device } from '@/services/Device';
import { resetCounter } from '@/services/invoiceNumberService';
import { hardwareBridge } from '@/services/hardwareBridge';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';

export default {
  name: 'StoreSelectionStep',
  data() {
    return {
      selectedStore: '',
      searchQuery: '',
      loading: false
    };
  },
  computed: {
    stores() {
      return this.$store.getters.getRegisteredStores || [];
    },
    filteredStores() {
      // Why: archived stores must not appear in the POS login flow.
      // Using `!== true` (not `=== true`) keeps stores with a missing
      // or undefined `archived` flag visible — safe default until the
      // backend confirms the field is populated on every store record.
      // Ticket #26903.
      const list = Array.isArray(this.stores)
        ? this.stores.filter(store => store?.archived !== true)
        : [];
      if (!this.searchQuery) return list;
      const query = this.searchQuery.toLowerCase();
      return list.filter(store =>
        store.StoreName?.toLowerCase().includes(query)
      );
    },
    loadText() {
      return this.loading ? 'Loading...' : 'Continue';
    }
  },
  async mounted() {
    // Keep franchise admin + store list; avoid localStorage.clear which raced
    // Switch Store and could drop session crumbs while Vuex already has admin.
    let device = await Device.getDeviceId();
    let stores = await Storage.getAllStores();
    if (Array.isArray(stores) && stores.length > 0) {
      await this.$store.commit('setRegisteredStores', stores);
    } else {
      await this.$store.commit('setRegisteredStores');
    }
    try {
      localStorage.setItem('deviceRegistrationID', device);
    } catch (_) { /* ignore */ }

    // Fetch stores by customer ID for validation
    try {
      await this.$store.dispatch('fetchStoresByCustomer');
    } catch (err) {
      console.warn('Failed to fetch customer stores:', err);
    }

    // Auto-select if only one store
    if (this.stores.length === 1) {
      this.selectedStore = this.stores[0];
      this.handleStoreSelection();
    }
  },
  methods: {
    selectStore(store) {
      this.selectedStore = store;
    },

    async logout() {
      // Full franchise leave from the store picker (not Switch Store).
      await this.$store.dispatch('signOutOfFranchise');
    },

    async syncLatestOfflineSequence(storeDb) {
      try {
        console.log('[StoreSelection] syncLatestOfflineSequence start', { storeDb });
        let deviceName = '';
        try {
          if (Capacitor.isNativePlatform() || window?.androidMode) {
            deviceName = await Device.getPCName(true);
          } else if (window?.hardwareIPC || window?.electronBridge) {
            const data = await hardwareBridge.getPcData();
            deviceName = data?.hostname || '';
          }
        } catch (e) {
          // ignore hardware bridge errors
        }

        console.log('[StoreSelection] offline/latest request', {
          url: serverApi + `invoice/${storeDb}/offline/latest?deviceName=${encodeURIComponent(deviceName)}`
        });
        const { data } = await axios.get(
          serverApi + `invoice/${storeDb}/offline/latest?deviceName=${encodeURIComponent(deviceName)}`
        );

        const latestSequence = Number(data?.latestSequence);
        console.log('[StoreSelection] offline/latest response', { latestSequence, data });
        if (Number.isFinite(latestSequence) && latestSequence >= 0) {
          // This store's sequence only (per-store counter key)
          await resetCounter(latestSequence, storeDb);
          try {
            localStorage.setItem('latestOfflineInvoiceSequence', String(latestSequence));
          } catch (e) {
            // ignore localStorage failures
          }
        }
      } catch (e) {
        // Non-blocking: continue even if server call fails
        console.warn('[StoreSelection] Failed to fetch latest offline invoice sequence:', e);
      }
    },

    async handleStoreSelection() {
      this.loading = true;

      try {
        const defaultStore = this.selectedStore;
        console.log('[StoreSelection] handleStoreSelection', { defaultStore });

        // Validate product ID before proceeding
        const customerStores = this.$store.getters.getCustomerStores;
        if (customerStores && customerStores.length > 0) {
          // Find matching store in customerStores by storeName or database
          const matchedStore = customerStores.find(store =>
            store.storeName === defaultStore.StoreName ||
            store.database === defaultStore.StoreDB
          );

          if (matchedStore && matchedStore.productId === 1 && matchedStore.productId !== 3) {
            // Product ID is 1 (Lytning), show error and redirect back to store selection
            this.loading = false;

            // Show error message
            if (this.$utils && this.$utils.showError) {
              this.$utils.showError('Your store is still under Lyt Soft. Please upgrade to the Lytning package to continue.');
            } else {
              alert('Your store is still under Lyt Soft. Please upgrade to the Lytning package to continue.');
            }

            // Stay on store selection (don't emit 'next')
            return;
          }
        }

        // Set database for hardware API if not in Android mode
        if (!androidMode) {
          try {
            await axios.get(hardwareApi + `utils/setDb/${defaultStore.StoreDB}`)
          } catch (e) {
            // Continue even if hardware API fails
          }
        }

        // Sync latest offline invoice sequence for this store
        if (defaultStore?.StoreDB) {
          await this.syncLatestOfflineSequence(defaultStore.StoreDB);
        } else {
          console.warn('[StoreSelection] Missing StoreDB, skipping offline sequence sync');
        }

        // Set default store in Vuex
        await this.$store.dispatch('setDefaultStore', defaultStore);

        // Emit success to parent
        this.$emit('next', { step: 'store-complete', data: { store: defaultStore } });
      } catch (err) {
        this.$emit('error', 'Failed to set store. Please try again.');
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.store-selection-step {
  padding: 0;
  width: 100%;
  background-color: var(--card-bg);
  border: var(--card-border);
  box-shadow: var(--card-shadow);
  border-radius: 16px;
  overflow: visible;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--card-shadow-hover);
  }
}

.card-title {
  font-weight: 700;
  letter-spacing: 1.2px;
  margin-bottom: 2rem;
  text-align: center;
  font-size: 1.5rem;
}

.text-primary-color {
  color: var(--text-primary);
}

.gradient-text {
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple), var(--accent-electric-blue));
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  opacity: 1;
}

.search-container {
  opacity: 1;
  transform: translateY(0);
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 1rem;
  color: var(--text-secondary);
  pointer-events: none;
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: 0.75rem 3rem 0.75rem 3rem;
  border: var(--card-border);
  border-radius: 12px;
  background-color: var(--card-bg);
  color: var(--text-primary);
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--accent-electric-blue);
    box-shadow: 0 0 0 3px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.15);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
}

.clear-search-btn {
  position: absolute;
  right: 1rem;
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;

  &:hover {
    color: var(--accent-electric-blue);
  }
}

.sign-out-container {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 0.5rem;
}

.sign-out-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.1);
  }
}

.store-form {
  margin-top: 1rem;
  opacity: 1;
  transform: translateY(0);
}

.form-group {
  position: relative;
}

.store-selection-label {
  display: block;
  margin-bottom: 1rem;
  color: var(--text-secondary);
  font-weight: 500;
  font-size: 1rem;
}

.store-list-container {
  max-height: 400px;
  overflow-y: auto;
  border-radius: 12px;
  border: var(--card-border);
  background-color: var(--card-bg);
}

.store-list {
  padding: 0.5rem;
}

.store-card {
  display: flex;
  align-items: center;
  padding: 1rem;
  margin-bottom: 0.5rem;
  border-radius: 12px;
  background-color: transparent;
  border: 2px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background-color: rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.05);
    border-color: rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.2);
  }

  &.selected {
    background-color: rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.1);
    border-color: var(--accent-electric-blue);
    box-shadow: 0 2px 8px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.2);
  }
}

.store-icon {
  width: 48px;
  height: 48px;
  min-width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;
  font-size: 1.5rem;
  margin-right: 1rem;
}

.store-info {
  flex: 1;
  min-width: 0;
}

.store-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
  word-break: break-word;
}

.store-checkmark {
  width: 32px;
  height: 32px;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: var(--accent-electric-blue);
  color: white;
  font-size: 1rem;
  margin-left: 0.5rem;
}

.store-list-container::-webkit-scrollbar {
  width: 8px;
}

.store-list-container::-webkit-scrollbar-track {
  background: transparent;
}

.store-list-container::-webkit-scrollbar-thumb {
  background: rgba(var(--text-secondary-rgb, 128, 128, 128), 0.3);
  border-radius: 4px;
}

.store-list-container::-webkit-scrollbar-thumb:hover {
  background: rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.5);
}

.no-results {
  text-align: center;
  padding: 2rem 1rem;
  color: var(--text-secondary);
  animation: fadeIn 0.3s ease;
}

.no-results-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.store-button {
  width: 100%;
  max-width: 350px;
  height: 56px;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 1.2px;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  margin: 1.5rem auto 0;
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;
  border: none;
  box-shadow: 0px 2px 8px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0px 4px 16px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg, 
      rgba(255, 255, 255, 0) 0%, 
      rgba(255, 255, 255, 0.3) 50%, 
      rgba(255, 255, 255, 0) 100%
    );
    transition: all 0.8s ease;
  }

  &:hover:not(:disabled)::before {
    left: 100%;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }

  .button-text {
    position: relative;
    z-index: 2;
  }

  .spinner-grow {
    margin-left: 8px;
    width: 1rem;
    height: 1rem;
  }
}

/* Animations removed for compatibility with reduced motion settings */

/* Responsive */
@media (max-width: 768px) {
  .card-body {
    padding: 1.5rem !important;
  }

  .store-button {
    max-width: 100%;
    font-size: 1rem;
  }

  .store-list-container {
    max-height: 350px;
  }

  .store-card {
    padding: 0.75rem;
  }

  .store-icon {
    width: 40px;
    height: 40px;
    min-width: 40px;
    font-size: 1.25rem;
    margin-right: 0.75rem;
  }

  .store-name {
    font-size: 1rem;
  }

  .store-checkmark {
    width: 28px;
    height: 28px;
    min-width: 28px;
    font-size: 0.9rem;
  }
}
</style>
